/**
 * Mux API Integration
 * Fetches videos from Mux API filtered by metadata via server-side API endpoint
 *
 * Features:
 * - Multi-layer caching: localStorage (persistent) + in-memory (fast)
 * - localStorage cache survives page reloads for same user
 * - Automatic cache invalidation after TTL
 * - Supports flexible metadata structure (can be in metadata object or passthrough)
 *
 * creator_id is read from brandConfig (VITE_BRAND_MUX_CREATOR_ID / VITE_BRAND_SOURCE_KEY)
 * so this works for any brand without code changes.
 */

import {
  BRAND_MUX_CREATOR_ID,
  BRAND_MUX_EXTERNAL_ID_FEED,
  BRAND_MUX_TEASER_TITLE,
} from "@/lib/brandConfig";

interface MuxApiResponse {
  playbackIds: string[];
  error?: string;
}

// In-memory cache with TTL (fast, but lost on page reload)
interface CacheEntry {
  data: string[];
  timestamp: number;
}

const memoryCache = new Map<string, CacheEntry>();
const MEMORY_CACHE_TTL = 5 * 60 * 1000; // 5 minutes (fast access)
const LOCALSTORAGE_CACHE_TTL = 60 * 60 * 1000; // 1 hour (persistent across reloads)
const LOCALSTORAGE_PREFIX = "mux_cache_";
const pendingRequests = new Map<string, Promise<string[]>>();

/**
 * Get data from localStorage cache
 */
function getFromLocalStorage(cacheKey: string): string[] | null {
  if (typeof window === "undefined" || !window.localStorage) {
    return null;
  }
  
  try {
    const stored = localStorage.getItem(LOCALSTORAGE_PREFIX + cacheKey);
    if (!stored) return null;
    
    const parsed = JSON.parse(stored) as CacheEntry;
    
    // Check if cache is still valid
    if (Date.now() - parsed.timestamp < LOCALSTORAGE_CACHE_TTL) {
      return parsed.data;
    }
    
    // Cache expired, remove it
    localStorage.removeItem(LOCALSTORAGE_PREFIX + cacheKey);
    return null;
  } catch (error) {
    console.warn("[muxApi] Failed to read from localStorage:", error);
    return null;
  }
}

/**
 * Save data to localStorage cache
 */
function saveToLocalStorage(cacheKey: string, data: string[]): void {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }
  
  try {
    const entry: CacheEntry = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(LOCALSTORAGE_PREFIX + cacheKey, JSON.stringify(entry));
  } catch (error) {
    console.warn("[muxApi] Failed to save to localStorage:", error);
  }
}

/**
 * Clear all Mux caches (useful for manual invalidation)
 */
export function clearMuxCache(): void {
  // Clear memory cache
  memoryCache.clear();
  
  // Clear localStorage cache
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(LOCALSTORAGE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
      if (import.meta.env.DEV) {
        console.log("[muxApi] All Mux caches cleared");
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn("[muxApi] Failed to clear localStorage cache:", error);
      }
    }
  }
}

/**
 * Get Mux videos filtered by metadata
 * Uses multi-layer caching: localStorage (persistent) + memory (fast) + deduplication
 */
export type MuxVideosMetadataQuery = {
  externalId?: string;
  creatorId?: string;
  /** Optional `meta.title` filter (e.g. `anaafernandez-teaser`). */
  title?: string | null;
};

export async function getMuxVideosWithMetadata(
  externalId: string = BRAND_MUX_EXTERNAL_ID_FEED,
  creatorId: string = BRAND_MUX_CREATOR_ID,
  title?: string | null,
): Promise<string[]> {
  const titleKey = title?.trim() || "";
  const cacheKey = `${externalId}:${creatorId}:${titleKey}`;
  
  // Layer 1: Check in-memory cache (fastest)
  const memCached = memoryCache.get(cacheKey);
  if (memCached && Date.now() - memCached.timestamp < MEMORY_CACHE_TTL) {
    if (import.meta.env.DEV) {
      console.log(`[muxApi] Memory cache hit for ${cacheKey}`);
    }
    return memCached.data;
  }
  
  // Layer 2: Check localStorage cache (survives page reloads)
  const localStorageCached = getFromLocalStorage(cacheKey);
  if (localStorageCached) {
    if (import.meta.env.DEV) {
      console.log(`[muxApi] localStorage cache hit for ${cacheKey}`);
    }
    // Also populate memory cache for faster subsequent access
    memoryCache.set(cacheKey, { data: localStorageCached, timestamp: Date.now() });
    return localStorageCached;
  }
  
  // Layer 3: Check if there's already a pending request for this key (deduplication)
  const pending = pendingRequests.get(cacheKey);
  if (pending) {
    if (import.meta.env.DEV) {
      console.log(`[muxApi] Reusing pending request for ${cacheKey}`);
    }
    return pending;
  }
  
  // Layer 4: Fetch from API
  const fetchPromise = (async () => {
    try {
      if (import.meta.env.DEV) {
        console.log(`[muxApi] Fetching from API for ${cacheKey}`);
      }
      const params = new URLSearchParams({
        external_id: externalId,
        creator_id: creatorId,
      });
      if (titleKey) params.set("title", titleKey);
      const apiUrl = `/api/mux-videos?${params.toString()}`;
      
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        if (import.meta.env.DEV) {
          console.warn(`[muxApi] API request failed with status ${response.status}`);
        }
        return [];
      }

      const data: MuxApiResponse = await response.json();
      const playbackIds = data.playbackIds || [];
      
      if (import.meta.env.DEV) {
        console.log(`[muxApi] API returned ${playbackIds.length} playback IDs for ${cacheKey}`);
      }
      
      // Store in both caches
      memoryCache.set(cacheKey, { data: playbackIds, timestamp: Date.now() });
      saveToLocalStorage(cacheKey, playbackIds);
      
      return playbackIds;
    } catch (error) {
      console.error(`[muxApi] Error fetching ${cacheKey}:`, error);
      return [];
    } finally {
      // Remove from pending
      pendingRequests.delete(cacheKey);
    }
  })();
  
  // Store as pending
  pendingRequests.set(cacheKey, fetchPromise);
  
  return fetchPromise;
}

/**
 * Get playback IDs for BaseTemplate gallery.
 * BaseTemplate usa siempre y solo: external_id "feed" y creator_id from brandConfig.
 */
/**
 * Playback ID del teaser VIP fullscreen vía metadata Mux:
 *   title: anaafernandez-teaser (BRAND_MUX_TEASER_TITLE)
 *   creator_id: anaafernandez
 *   external_id: feed
 */
export async function getMuxTeaserPlaybackId(): Promise<string | null> {
  const playbackIds = await getMuxVideosWithMetadata(
    BRAND_MUX_EXTERNAL_ID_FEED,
    BRAND_MUX_CREATOR_ID,
    BRAND_MUX_TEASER_TITLE,
  );
  return playbackIds[0] ?? null;
}

export async function getBaseTemplateMuxPlaybackIds(): Promise<string[]> {
  const playbackIds = await getMuxVideosWithMetadata(
    BRAND_MUX_EXTERNAL_ID_FEED,
    BRAND_MUX_CREATOR_ID,
  );
  return playbackIds;
}

/**
 * Playback IDs for private /reels page.
 * Same Mux pool as BaseTemplate gallery:
 *   external_id: feed (BRAND_MUX_EXTERNAL_ID_FEED)
 *   creator_id: brand (BRAND_MUX_CREATOR_ID → anaafernandez)
 */
export async function getReelsMuxPlaybackIds(): Promise<string[]> {
  const playbackIds = await getMuxVideosWithMetadata(
    BRAND_MUX_EXTERNAL_ID_FEED,
    BRAND_MUX_CREATOR_ID,
  );
  return playbackIds;
}
